import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { KeyValuePipe, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, DestroyRef, inject, signal, TemplateRef, viewChild, ViewContainerRef } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { forOwn, sortBy, uniq } from 'lodash-es';
import { catchError, concat, EMPTY, last, Observable, of, Subscription, switchMap, tap, throwError } from 'rxjs';
import { LoadingComponent } from '../../../component/loading/loading.component';
import { FakeLinkDirective } from '../../../directive/fake-link.directive';
import { DiffComponent } from '../../../form/diff/diff.component';
import { Config, Mod } from '../../../model/tag';
import { AdminService } from '../../../service/admin.service';
import { ModService } from '../../../service/mod.service';
import { Store } from '../../../store/store';
import { equalBundle, mergeBundle } from '../../../util/diff';
import { controlState, scrollToFirstInvalid } from '../../../util/form';
import { configGroups, formSafeNames, modId } from '../../../util/format';
import { printError } from '../../../util/http';

interface ModUpdatePreview {
  mod: string;
  proposed: Mod;
  diffBase: Mod;
  conflict: boolean;
}

@Component({
  selector: 'app-settings-setup-page',
  templateUrl: './setup.component.html',
  styleUrls: ['./setup.component.scss'],
  imports: [
    FakeLinkDirective,
    ReactiveFormsModule,
    RouterLink,
    KeyValuePipe,
    NgTemplateOutlet,
    DiffComponent,
    OverlayModule,
    LoadingComponent,
  ],
})
export class SettingsSetupPage {
  admin = inject(AdminService);
  private mod = inject(ModService);
  store = inject(Store);
  private fb = inject(UntypedFormBuilder);
  private overlay = inject(Overlay);
  private vc = inject(ViewContainerRef);


  readonly submitted = signal<boolean>(false);
  readonly serverError = signal<string[]>([]);
  readonly installMessages = signal<string[]>([]);
  readonly mergeState = signal<ModUpdatePreview | undefined>(undefined);
  readonly mergeSaving = signal(false);
  readonly modChangesLoaded = signal(false);
  readonly modChangesError = signal<string[]>([]);
  readonly hasLog = computed(() => !!(this.modChangesError().length || this.serverError().length || this.installMessages().length));
  private mergeSavingSubscription?: Subscription;
  private modChangesSubscription?: Subscription;

  readonly mergePopup = viewChild<TemplateRef<any>>('mergePopup');

  experiments = !!this.admin.getTemplate('config/experiments');
  readonly selectAllToggle = signal<boolean>(false);
  adminForm: UntypedFormGroup;
  protected readonly adminFormValid = controlState(() => this.adminForm, c => c.valid);
  mergePopupSub = new Subscription();
  modGroups = configGroups({
    ...this.admin.status().disabledPlugins, ...this.admin.status().disabledTemplates,
    ...this.admin.status().plugins, ...this.admin.status().templates,
    ...this.admin.def.plugins, ...this.admin.def.templates });

  private mergePopupRef?: OverlayRef;

  constructor() {
    const mod = this.mod;
    const fb = this.fb;

    mod.setTitle($localize`Settings: Setup`);
    this.adminForm = fb.group({
      mods: fb.group(formSafeNames({...this.admin.def.plugins, ...this.admin.def.templates })),
    });
    this.clear();
    this.loadModChanges(this.admin.ready$.pipe(tap(() => this.clear())));
  }

  private readonly destroyCleanup = inject(DestroyRef).onDestroy(() => {
    this.cancelMerge();
    this.modChangesSubscription?.unsubscribe();
  });

  loadModChanges(ready$: Observable<any> = this.admin.ready$) {
    this.modChangesSubscription?.unsubscribe();
    this.modChangesLoaded.set(false);
    this.modChangesError.set([]);
    this.modChangesSubscription = ready$.pipe(
      switchMap(() => this.admin.loadModChanges$()),
      catchError((res: HttpErrorResponse) => {
        this.modChangesError.set(printError(res));
        return EMPTY;
      }),
    ).subscribe(() => this.modChangesLoaded.set(true));
  }

  install() {
    this.serverError.set([]);
    this.installMessages.set([]);
    this.submitted.set(true);
    this.adminForm.markAllAsTouched();
    if (!this.adminForm.valid) {
      scrollToFirstInvalid();
      return;
    }
    const deletes: string[] = [];
    const installs: string[] = [];
    for (const plugin in this.admin.def.plugins) {
      const formName = plugin.replace(/[.]/g, '-');
      const def = this.admin.def.plugins[plugin];
      const status = this.admin.status().plugins[plugin] || this.admin.status().disabledPlugins[plugin];
      if (!!status === !!this.adminForm.value.mods[formName]) continue;
      if (this.adminForm.value.mods[formName]) {
        installs.push(modId(def));
      } else {
        deletes.push(modId(status));
      }
    }
    for (const template in this.admin.def.templates) {
      const formName = template.replace(/[.]/g, '-');
      const def = this.admin.def.templates[template];
      const status = this.admin.status().templates[template] || this.admin.status().disabledTemplates[template];
      if (!!status === !!this.adminForm.value.mods[formName]) continue;
      if (this.adminForm.value.mods[formName]) {
        installs.push(modId(def));
      } else {
        deletes.push(modId(status));
      }
    }
    const _ = (msg?: string) => this.installMessages.update(messages => msg ? [...messages, msg] : messages);
    if (!deletes.length && !installs.length) {
      this.submitted.set(true);
      _($localize`Success.`);
      return;
    }
    concat(
      ...uniq(deletes).map(m => this.admin.deleteMod$(m, _)),
      ...this.receiptsFirst(uniq(installs)).map(m => this.admin.installMod$(m, _))
    ).pipe(
      catchError((res: HttpErrorResponse) => {
        this.serverError.set(printError(res));
        return throwError(() => res);
      }),
      last(),
    ).subscribe(() => {
      this.submitted.set(true);
      this.reset();
      _($localize`Success.`);
    });
  }

  reset() {
    this.loadModChanges(this.admin.reload$().pipe(tap(() => {
      this.clear();
      this.installMessages.update(messages => [...messages, $localize`Reload the page to finish loading mod changes.`]);
    })));
  }

  clear() {
    this.adminForm.reset({
      mods: formSafeNames({
        ...this.admin.status().plugins,
        ...this.admin.status().disabledPlugins,
        ...this.admin.status().templates,
        ...this.admin.status().disabledTemplates,
      }),
    });
  }

  updateAll() {
    const _ = (msg?: string) => this.installMessages.update(messages => msg ? [...messages, msg] : messages);
    const mods: string[] = [];
    for (const plugin in this.admin.status().plugins) {
      const m = modId(this.admin.status().plugins[plugin]);
      if (this.store.view.modUpdates().has(m)) mods.push(m);
    }
    for (const template in this.admin.status().templates) {
      const m = modId(this.admin.status().templates[template]);
      if (this.store.view.modUpdates().has(m)) mods.push(m);
    }
    if (!mods.length) return;
    concat(...this.receiptsFirst(uniq(mods)).map(mod => {
      const receipt = this.admin.getMod(mod)!;
      if (!this.hasCustomChangesMod(mod)) {
        return this.admin.updateMod$(mod, receipt, receipt, _);
      }
      const diff = this.getModDiff(mod);
      if (diff.conflict) {
        _($localize`⚠️ Skipped ${mod} mod: unable to merge local changes automatically. Use diff to update manually.`);
        return of(null);
      } else {
        return this.admin.updateMod$(mod, diff.proposed, receipt, _);
      }
    })).pipe(
      catchError((res: HttpErrorResponse) => {
        this.serverError.set(printError(res));
        return throwError(() => res);
      }),
    ).pipe(last()).subscribe(() => {
      this.submitted.set(true);
      this.reset();
      _($localize`Success.`);
    });
  }

  /**
   * Install the mod providing plugin/mod/receipt first, so every other mod in the batch logs a receipt.
   */
  private receiptsFirst(mods: string[]) {
    return sortBy(mods, m => this.admin.getMod(m)?.plugin?.some(p => p.tag === 'plugin/mod/receipt') ? 0 : 1);
  }

  selectAll() {
    this.selectAllToggle.set(!this.selectAllToggle());
    forOwn((this.adminForm.get('mods') as UntypedFormGroup).controls, c => {
      c.setValue(this.selectAllToggle());
    });
  }

  updateMod(config: Config) {
    const mod = modId(config);
    const receipt = this.admin.getMod(mod)!;
    const _ = (msg?: string) => this.installMessages.update(messages => msg ? [...messages, msg] : messages);
    if (!this.admin.getTemplate('config/diff') || !this.hasCustomChangesMod(mod)) {
      this.admin.updateMod$(mod, receipt, receipt, _).subscribe(() => {
        this.reset();
        _($localize`Success.`);
      });
      return;
    }
    this.mergeState.set(this.getModDiff(modId(config)));
    if (this.mergeState()?.conflict) {
      this.openMergePopup();
    } else {
      this.admin.updateMod$(mod, this.mergeState()!.proposed, receipt, _).subscribe(() => {
        this.reset();
        _($localize`Success.`);
      });
    }
  }

  diffMod(config: Config) {
    this.serverError.set([]);
    this.mergeState.set(this.getModDiff(modId(config)));
    this.openMergePopup();
  }

  private getModDiff(mod: string): ModUpdatePreview {
    const current = this.admin.getInstalledMod(mod);
    if (!current) throw new Error(`Mod ${mod} not installed`);
    const target = this.admin.getMod(mod)!;
    const base = this.admin.status().receipts[mod]?.plugins?.['plugin/mod'];
    if (!base) {
      if (equalBundle(current, target)) {
        return {
          mod,
          proposed: target,
          diffBase: current,
          conflict: false,
        };
      }
      return {
        mod,
        proposed: current,
        diffBase: target,
        conflict: true,
      };
    }
    if (!equalBundle(current, base)) {
      const merged = mergeBundle(current, base, target);
      if (!merged.result || merged.conflict) {
        return {
          mod,
          proposed: current,
          diffBase: target,
          conflict: true,
        };
      }
      return {
        mod,
        proposed: merged.result,
        diffBase: target,
        conflict: false,
      };
    }
    return {
      mod,
      proposed: target,
      diffBase: current,
      conflict: false,
    };
  }

  needsModUpdate(config: Config) {
    return this.store.view.modUpdates().has(modId(config));
  }

  hasCustomChanges(config: Config) {
    return this.store.view.modChanges().get(modId(config));
  }

  hasCustomChangesMod(mod: string) {
    return this.store.view.modChanges().get(mod);
  }

  canDiffMod(config: Config) {
    if (!this.admin.getTemplate('config/diff')) return;
    return this.needsModUpdate(config) || this.hasCustomChanges(config);
  }

  private openMergePopup() {
    const mergePopup = this.mergePopup();
    if (!mergePopup || this.mergePopupRef?.hasAttached()) return;
    this.mergePopupRef = this.overlay.create({
      height: window.visualViewport?.height ? window.visualViewport.height + 'px' : '100vh',
      width: '100vw',
      hasBackdrop: true,
      positionStrategy: this.overlay.position()
        .global()
        .centerHorizontally()
        .top('0'),
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });
    this.mergePopupRef.attach(new TemplatePortal(mergePopup, this.vc));
    this.mergePopupSub.add(this.mergePopupRef.backdropClick().subscribe(() => this.cancelMerge()));
    this.mergePopupSub.add(this.mergePopupRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') this.cancelMerge();
    }));
  }

  installed(config: Config) {
    const mod = modId(config);
    return Object.values(this.admin.status().plugins).find(p => p && mod === modId(p)) ||
      Object.values(this.admin.status().templates).find(t => t && mod === modId(t));
  }

  disabled(config: Config) {
    const mod = modId(config);
    return Object.values(this.admin.status().disabledPlugins).find(p => p && mod === modId(p)) ||
      Object.values(this.admin.status().disabledTemplates).find(t => t && mod === modId(t));
  }

  modLabel([tag, e]: [string, Config]) {
    return (e.config?.mod?.replace(/\W/g, '') || tag).toLowerCase();
  }

  applyMerge(bundle: Mod | null) {
    if (!this.mergeState() || !bundle) return;
    this.serverError.set([]);
    const _ = (msg?: string) => this.installMessages.update(messages => msg ? [...messages, msg] : messages);
    this.mergeSaving.set(true);
    this.mergeSavingSubscription = this.admin.updateMod$(this.mergeState()!.mod, bundle, this.admin.getMod(this.mergeState()!.mod)!, _)
      .pipe(catchError((res: HttpErrorResponse) => {
        this.mergeSaving.set(false);
        return throwError(() => res);
      }))
      .subscribe(() => {
        this.cancelMerge();
        this.reset();
        _($localize`Success.`);
      });
    this.mergeSavingSubscription?.add(() => this.mergeSaving.set(false));
  }

  cancelMerge() {
    this.mergeState.set(undefined);
    this.mergeSavingSubscription?.unsubscribe();
    this.mergeSaving.set(false);
    this.mergePopupSub.unsubscribe();
    this.mergePopupSub = new Subscription();
    this.mergePopupRef?.dispose();
    this.mergePopupRef = undefined;
  }
}
